<template>
  <div class="ui-base-dropdown" ref="root">
    <button
      role="button"
      ref="trigger"
      tabindex="0"
      class="ui-base-dropdown-button layout-row"
      @click="toggle"
      aria-haspopup="true"
    >
      <slot name="trigger" />
    </button>
    <ClientOnly>
      <div
        ref="dropdownMenu"
        class="ui-base-dropdown-menu"
        :style="{ 'min-width': width }"
        :aria-hidden="!isActive"
      >
        <transition name="fade">
          <div
            v-if="isActive"
            class="ui-base-dropdown-body"
            role="menu"
          >
            <div
              tabindex="-1"
              v-trap-focus="true"
            >
              <slot />
            </div>
          </div>
        </transition>
      </div>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import vTrapFocus, { focusFirst } from '~/directives/trap-focus'
import { createPopper } from '@popperjs/core'
import type { Placement } from '@popperjs/core'

interface Props {
  placement?: Placement
  width?: string
}
const props = withDefaults(defineProps<Props>(), {
  placement: 'bottom-end',
  width: 'max-content',
})

const isActive = ref(false)
let popperJS

const trigger = ref<HTMLElement>(null)
const dropdownMenu = ref<HTMLElement>(null)

onMounted(async () => {
  await nextTick()
  popperJS = createPopper(trigger.value, dropdownMenu.value, {
    placement: props.placement,
    modifiers: [
      {
        name: 'computeStyles',
        options: {
          gpuAcceleration: true,
          adaptive: false,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 2],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
      {
        name: 'flip',
      },
    ],
  })
  document.addEventListener('keydown', keydownEscape)
  document.addEventListener('click', handleDocumentClick)
})

function keydownEscape(e) {
  if (e.key === 'Escape') {
    close()
  }
}

function toggle() {
  if (!isActive.value) {
    isActive.value = true
    nextTick(() => {
      popperJS.update()
      focusFirst(dropdownMenu.value)
    })
  } else {
    close()
  }
}

function close() {
  if (isActive.value) {
    isActive.value = false
    trigger.value.focus()
  }
}

const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    isActive.value = false
  },
)

provide('close', close)

onUnmounted(() => {
  document.removeEventListener('keydown', keydownEscape)
  document.removeEventListener('click', handleDocumentClick)
  if (popperJS) {
    popperJS.destroy()
    popperJS = null
  }
})

const root = ref<HTMLElement>(null)

function handleDocumentClick(e: any) {
  if (!root.value || elementContains(root.value, e.target)) {
    return
  }
  close()
}

function elementContains(elm: HTMLElement, otherElm: HTMLElement) {
  if (typeof elm.contains === 'function') {
    return elm.contains(otherElm)
  }
  return false
}
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

// sample.vueで使用。グローバルに移動するか？
.base-dropdown-separator {
  margin-bottom: calc(0.25rem * 1);
  margin-top: calc(0.25rem * 1);
  display: block;
  height: 0.5px;
  width: 100%;
  background-color: $jps-gray-300;
}

.ui-base-dropdown {
  position: relative;
  display: inline-flex;
  width: min-content;
}

.ui-base-dropdown-button {
 position: relative;
 cursor: pointer;
}

.ui-base-dropdown-menu {
  position: fixed;
  z-index: 999;
}

.ui-base-dropdown-body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.25rem;
  border-width: 1px;
  border-color: #c1c1c1;
  border-style: solid;
  background-color: $white;
  filter: drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 2px rgb(0 0 0 / 0.06));
}
</style>
